import React, { useState, useMemo, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import PropTypes from "prop-types";

import { DateRangePicker } from "react-dates";
import { Row, Col } from "react-bootstrap";
import moment from "moment";
import { orderBy } from "lodash";

import { DropdownSearchSelect } from "../../../common/DropdownSearch";

import * as departmentActions from "../../../../actions/departmentActions";
import * as customerActions from "../../../../actions/customerActions";
import * as projectActions from "../../../../actions/projectActions";
import * as reportActions from "../../../../actions/reportActions";

import ReportFilters from "../../ReportFiltersComponent";

const INITIAL_FILTERS_STATE = {
  department: {},
  customer: null,
  project: null,
  arrival_date: null,
  completed_date: null
};

const EmployeeSummaryByProjectFilters = ({
  departments,
  customers,
  projects,
  actions,
  loading,
  auth
}) => {
  const [filters, setFilters] = useState(INITIAL_FILTERS_STATE);
  const [focusedInput, setFocusedInput] = useState(null);

  const getCurrentUserDepartment = useCallback(() => {
    if (auth.user?.department) {
      const { id, description } = auth.user.department;
      return { label: description, value: id };
    }
    return [];
  }, [auth.user]);

  useEffect(() => {
    actions.loadDepartments();
    actions.loadCustomers();
  }, [actions]);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      department: getCurrentUserDepartment()
    }));
  }, [getCurrentUserDepartment]);

  useEffect(() => {
    if (filters.customer?.value) {
      actions.loadProjects({
        customer: filters.customer.value,
        active: true
      });
      setFilters((prev) => ({ ...prev, project: null }));
      actions.clearReport();
    }
  }, [actions, filters.customer]);

  const handleChangeDate = (startDate, endDate) => {
    setFilters((prev) => ({
      ...prev,
      ...(startDate && { arrival_date: moment(startDate).startOf("day") }),
      ...(endDate && { completed_date: moment(endDate).endOf("day") })
    }));
  };

  const handleChange = (name, data) => {
    setFilters((prev) => ({ ...prev, [name]: data }));
  };

  const handleResetFilters = () => {
    const newFilters = {
      customer: null,
      project: null,
      department: getCurrentUserDepartment(),
      arrival_date: null,
      completed_date: null
    };
    setFilters(newFilters);
    actions.clearReport();
  };

  const fetchData = useCallback(async () => {
    if (filters.project?.value) {
      actions.loadProjectEmployeeSummary({
        filtered: {
          project: filters.project.value,
          department: filters.department?.value,
          arrival_date: filters.arrival_date,
          completed_date: filters.completed_date
        }
      });
    }
  }, [actions, filters]);

  const departmentOptions = useMemo(() => {
    const options = departments.map((dep) => ({
      value: dep._id,
      label: dep.description
    }));
    return orderBy(options, [(option) => option.label.toLowerCase()]);
  }, [departments]);

  const customerOptions = useMemo(() => {
    const options = customers.map((customer) => ({
      value: customer._id,
      label: customer.name
    }));
    return orderBy(options, [(option) => option.label.toLowerCase()]);
  }, [customers]);

  const projectOptions = useMemo(() => {
    const options = projects.map((project) => ({
      value: project._id,
      label: project.name
    }));
    return orderBy(options, [(option) => option.label.toLowerCase()]);
  }, [projects]);

  return (
    <ReportFilters
      onFetchClick={fetchData}
      onResetClick={handleResetFilters}
      title="Employee Summary By Project"
      loading={loading}
      csvExportAvailable={false}
      disabled={!filters.project}
    >
      <Row>
        <Col md={3}>
          <DropdownSearchSelect
            isClearable
            label
            fieldLabel="Department"
            placeholder="Select"
            name="department"
            onChange={handleChange}
            value={filters.department}
            data={departmentOptions}
          />
        </Col>
        <Col md={3}>
          <DropdownSearchSelect
            label
            fieldLabel="Customer *"
            placeholder="Select"
            name="customer"
            onChange={handleChange}
            value={filters.customer}
            data={customerOptions}
          />
        </Col>
        <Col md={3}>
          <DropdownSearchSelect
            label
            fieldLabel="Project *"
            placeholder="Select"
            name="project"
            onChange={handleChange}
            value={filters.project}
            data={projectOptions}
          />
        </Col>
        <Col md={3}>
          <div style={{ marginTop: 8 }}>
            <label
              className="control-label"
              style={{ textTransform: "uppercase" }}
            >
              Date Range
            </label>
            <DateRangePicker
              startDate={
                filters.arrival_date ? moment(filters.arrival_date) : null
              }
              startDateId="startDate"
              endDate={
                filters.completed_date ? moment(filters.completed_date) : null
              }
              endDateId="endDate"
              onDatesChange={({ startDate, endDate }) => {
                handleChangeDate(startDate, endDate);
              }}
              isOutsideRange={() => false}
              focusedInput={focusedInput}
              onFocusChange={setFocusedInput}
            />
          </div>
        </Col>
      </Row>
      <Row>
        <Col md={3}>
          <label
            className="control-label"
            style={{ textTransform: "uppercase" }}
          >
            * Required Fields
          </label>
        </Col>
      </Row>
    </ReportFilters>
  );
};

EmployeeSummaryByProjectFilters.propTypes = {
  departments: PropTypes.array,
  customers: PropTypes.array,
  projects: PropTypes.array,
  actions: PropTypes.object,
  loading: PropTypes.bool,
  auth: PropTypes.object
};

function mapStatesToProps(state) {
  return {
    departments: state.reducers.departments,
    customers: state.reducers.customers,
    projects: state.reducers.projects,
    loading: state.reducers.loading,
    auth: state.reducers.auth
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...departmentActions,
        ...customerActions,
        ...projectActions,
        ...reportActions
      },
      dispatch
    )
  };
}

export default connect(
  mapStatesToProps,
  mapDispatchToProps
)(EmployeeSummaryByProjectFilters);
